import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { StackedColorBar } from './style';
import { abyssColors } from '@/theme/colors';

type StackedBarChartProps = {
  data: Array<{ _id: string; count: number }>;
  displayConfig: Array<{ fieldValue: string; color: string }>;
};

export const StackedBarChart: React.FC<StackedBarChartProps> = ({ data, displayConfig }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>('');

  // Calculate the total count within the component
  const totalCount = useMemo(() => {
    return data.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
  }, [data]);

  const handleChangeCategory = useCallback(
    (categoryName?: string) => {
      setSelectedCategory((previousCategory) =>
        previousCategory === categoryName ? '' : categoryName
      );
    },
    [setSelectedCategory]
  );

  const getCategoryColor = useCallback(
    (categoryName: string) => {
      const config = displayConfig.find((fieldConfig) => fieldConfig.fieldValue === categoryName);
      return config ? config.color : '#CCCCCC'; // Default color if not found
    },
    [displayConfig]
  );

  if (data.length === 0)
    return (
      <Typography
        variant="h6"
        fontWeight={500}
        sx={{
          color: abyssColors.primary[400],
          whiteSpace: 'nowrap',
          mt: 1,
        }}
      >
        No data found
      </Typography>
    );

  return (
    <>
      <Stack direction="row" spacing={0.1} sx={{ mb: 2 }}>
        {data.map(({ _id: name, count }, index) => (
          <StackedColorBar
            key={`${name}-${count}`}
            widthPercentage={(count / totalCount) * 100}
            selected={selectedCategory === name}
            color={getCategoryColor(name)}
            first={index === 0}
            last={index === data?.length - 1}
            onClick={() => handleChangeCategory(name)}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        {data.map(({ _id: name, count }) => (
          <Box
            key={`${name}-${count}`}
            sx={{ cursor: 'pointer', paddingRight: '15px' }}
            onClick={() => handleChangeCategory(name)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {count}
            </Typography>
            <Stack direction="row" alignItems="center">
              <Box
                sx={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: getCategoryColor(name),
                  borderRadius: '50%',
                  marginRight: '4px',
                }}
              />
              <Typography variant="caption">{name}</Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </>
  );
};
